import * as React from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";

import { Colors } from "../../utils/Colors";

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */

const http = axios.create({
  baseURL: "http://127.0.0.1:8000",
  timeout: 20000,
});

const API = {
  fluidSummary: "/api/medcharts/fluid-therapy-patient-summary/?format=json",
  fluidHistory: "/api/medcharts/fluid-therapy-patient-history/?format=json",

  medicationSummary: "/api/medcharts/medication-patient-summary/?format=json",
  medicationHistory: "/api/medcharts/medication-patient-history/?format=json",

  investigationSummary: "/api/investigations/patient-summary/",
  investigationHistory: "/api/investigations/patient-history/",
};

/* -------------------------------------------------------------------------- */
/*                                  ANIMATIONS                                */
/* -------------------------------------------------------------------------- */

const softPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 8px 20px rgba(11,60,63,0.06); }
  50% { transform: scale(1.015); box-shadow: 0 14px 26px rgba(11,60,63,0.12); }
  100% { transform: scale(1); box-shadow: 0 8px 20px rgba(11,60,63,0.06); }
`;

const shimmer = keyframes`
  0% { background-position: -220px 0; }
  100% { background-position: 220px 0; }
`;

const growBar = keyframes`
  0% { transform: scaleY(0.65); opacity: 0.7; }
  50% { transform: scaleY(1); opacity: 1; }
  100% { transform: scaleY(0.65); opacity: 0.7; }
`;

/* -------------------------------------------------------------------------- */
/*                               HELPER FUNCTIONS                             */
/* -------------------------------------------------------------------------- */

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function fmt(value) {
  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}

function normalizeKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
}

function getLatestDate(...values) {
  const valid = values
    .filter(Boolean)
    .map((v) => new Date(v))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (!valid.length) return "";
  valid.sort((a, b) => b.getTime() - a.getTime());
  return valid[0].toISOString();
}

function getFluidLabel(fluid) {
  const map = {
    NS: "Normal Saline",
    RL: "Ringer Lactate",
    DNS: "Dextrose Normal Saline",
    D5: "5% Dextrose",
    D10: "10% Dextrose",
    OTHER: "Other",
  };
  return map[fluid] || fluid || "—";
}

function getMedicationTypeLabel(type) {
  const map = {
    ANTIBIOTIC: "Antibiotic",
    ANALGESIC: "Analgesic",
    ANTIPYRETIC: "Antipyretic",
    ANTACID: "Antacid",
    ANTIHYPERTENSIVE: "Antihypertensive",
    ANTIDIABETIC: "Antidiabetic",
    INSULIN: "Insulin",
    IV_FLUID: "IV Fluid",
    VITAMIN: "Vitamin",
    OTHER: "Other",
  };
  return map[type] || type || "—";
}

function getRouteLabel(route) {
  const map = {
    ORAL: "Oral",
    IV: "IV",
    IM: "IM",
    SC: "SC",
    TOPICAL: "Topical",
    INHALATION: "Inhalation",
    RECTAL: "Rectal",
    NASAL: "Nasal",
    OTHER: "Other",
  };
  return map[route] || route || "—";
}

function getFrequencyLabel(frequency) {
  const map = {
    OD: "OD - Once Daily",
    BD: "BD - Twice Daily",
    TDS: "TDS - Three Times Daily",
    QID: "QID - Four Times Daily",
    SOS: "SOS - As Needed",
    STAT: "STAT - Immediately",
    Q4H: "Every 4 Hours",
    Q6H: "Every 6 Hours",
    Q8H: "Every 8 Hours",
    Q12H: "Every 12 Hours",
  };
  return map[frequency] || frequency || "—";
}

function getStatusColor(status) {
  const v = String(status || "").toLowerCase();
  if (v.includes("completed")) return Colors.status.success;
  if (v.includes("pending")) return Colors.status.warning;
  if (v.includes("ordered")) return Colors.status.info;
  if (v.includes("abnormal")) return Colors.status.danger;
  return Colors.brand.primary;
}

function buildMergedPatientMap(fluidPatients, medicationPatients, investigationPatients) {
  const map = new Map();

  function ensurePatient(base) {
    const key = normalizeKey(base.patient_uhid);
    if (!key) return null;

    if (!map.has(key)) {
      map.set(key, {
        patient_uhid: base.patient_uhid || "",
        patient_name: base.patient_name || "",
        admission_number: base.admission_number || "",
        doctor_name: base.doctor_name || "",
        total_fluid_times: 0,
        total_medications: 0,
        total_investigations: 0,
        last_fluid_at: "",
        last_medication_at: "",
        last_investigation_at: "",
      });
    }

    const row = map.get(key);

    if (!row.patient_name && base.patient_name) row.patient_name = base.patient_name;
    if (!row.admission_number && base.admission_number) row.admission_number = base.admission_number;
    if (!row.doctor_name && base.doctor_name) row.doctor_name = base.doctor_name;

    return row;
  }

  fluidPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_fluid_times = Number(row.total_fluid_times || 0);
    patient.last_fluid_at = row.last_fluid_at || "";
  });

  medicationPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_medications = Number(row.total_medications || 0);
    patient.last_medication_at = row.last_medication_at || "";
  });

  investigationPatients.forEach((row) => {
    const patient = ensurePatient(row);
    if (!patient) return;
    patient.total_investigations = Number(row.total_investigations || 0);
    patient.last_investigation_at = row.last_investigation_at || "";
  });

  return Array.from(map.values())
    .map((row, index) => ({
      id: `${row.patient_uhid}-${index}`,
      ...row,
      last_activity_at: getLatestDate(
        row.last_fluid_at,
        row.last_medication_at,
        row.last_investigation_at
      ),
    }))
    .sort((a, b) => {
      const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
      const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
      return bTime - aTime;
    });
}

/* -------------------------------------------------------------------------- */
/*                            REUSABLE UI PARTS                               */
/* -------------------------------------------------------------------------- */

function Panel({ title, icon, right, children, sx = {} }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        background: Colors.bg.paper,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        height: "100%",
        overflow: "hidden",
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 1.4, md: 1.8 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography
              sx={{
                fontSize: { xs: 15, md: 17 },
                fontWeight: 900,
                color: Colors.text.title,
              }}
            >
              {title}
            </Typography>
          </Stack>
          {right}
        </Stack>

        <Divider sx={{ borderColor: Colors.border.light, mb: 1.4 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function DetailLine({ label, value }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        py: 0.55,
        borderBottom: `1px solid ${Colors.border.topLine}`,
      }}
    >
      <Typography
        sx={{
          width: 98,
          minWidth: 98,
          fontSize: 11,
          fontWeight: 800,
          color: Colors.text.muted,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: Colors.text.body,
          wordBreak: "break-word",
          lineHeight: 1.3,
        }}
      >
        {fmt(value)}
      </Typography>
    </Stack>
  );
}

function KpiCard({ icon, label, value, sub, color, bg }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        background: bg,
        boxShadow: Colors.shadow.brand,
        animation: `${softPulse} 4.2s ease-in-out infinite`,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800 }}>
              {label}
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: { xs: 24, md: 30 },
                fontWeight: 900,
                color: Colors.text.title,
                lineHeight: 1.1,
              }}
            >
              {fmt(value)}
            </Typography>
            {sub ? (
              <Typography sx={{ mt: 0.5, fontSize: 11, color: Colors.text.sub, fontWeight: 700 }}>
                {sub}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "14px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionEmpty({ text }) {
  return (
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      {text}
    </Alert>
  );
}

function MiniCountChart({ fluidCount = 0, medicationCount = 0, investigationCount = 0 }) {
  const values = [
    { label: "Fluid", value: Number(fluidCount || 0), color: Colors.status.success },
    { label: "Medicine", value: Number(medicationCount || 0), color: Colors.status.info },
    { label: "Investigation", value: Number(investigationCount || 0), color: Colors.status.warning },
  ];

  const max = Math.max(...values.map((x) => x.value), 1);

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: Colors.bg.hover,
      }}
    >
      <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800, mb: 1.2 }}>
        Clinical Record Distribution
      </Typography>

      <Stack direction="row" alignItems="end" justifyContent="space-between" spacing={2} sx={{ height: 150 }}>
        {values.map((item, index) => {
          const height = Math.max(18, Math.round((item.value / max) * 100));
          return (
            <Stack key={index} spacing={0.8} alignItems="center" sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 900, color: Colors.text.title }}>
                {item.value}
              </Typography>

              <Box
                sx={{
                  width: "70%",
                  height: `${height}px`,
                  minHeight: 18,
                  borderRadius: "14px 14px 6px 6px",
                  bgcolor: item.color,
                  animation: `${growBar} ${1.8 + index * 0.2}s ease-in-out infinite`,
                  boxShadow: `0 10px 18px ${alpha(item.color, 0.22)}`,
                }}
              />

              <Typography
                sx={{
                  fontSize: 11,
                  color: Colors.text.body,
                  fontWeight: 800,
                  textAlign: "center",
                }}
              >
                {item.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

function SummaryStrip({ patient }) {
  const fluid = Number(patient?.total_fluid_times || 0);
  const meds = Number(patient?.total_medications || 0);
  const inv = Number(patient?.total_investigations || 0);
  const total = fluid + meds + inv || 1;

  const fluidPct = Math.round((fluid / total) * 100);
  const medPct = Math.round((meds / total) * 100);
  const invPct = Math.round((inv / total) * 100);

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800, mb: 1 }}>
        Overall Clinical Mix
      </Typography>

      <Box
        sx={{
          width: "100%",
          height: 16,
          borderRadius: 999,
          overflow: "hidden",
          display: "flex",
          border: `1px solid ${Colors.border.light}`,
          bgcolor: Colors.bg.hover,
        }}
      >
        <Box sx={{ width: `${fluidPct}%`, bgcolor: Colors.status.success }} />
        <Box sx={{ width: `${medPct}%`, bgcolor: Colors.status.info }} />
        <Box sx={{ width: `${invPct}%`, bgcolor: Colors.status.warning }} />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
        <LegendDot color={Colors.status.success} label={`Fluid ${fluidPct}%`} />
        <LegendDot color={Colors.status.info} label={`Medication ${medPct}%`} />
        <LegendDot color={Colors.status.warning} label={`Investigation ${invPct}%`} />
      </Stack>
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Stack direction="row" spacing={0.7} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
      <Typography sx={{ fontSize: 11, color: Colors.text.body, fontWeight: 800 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function HistoryTable({ columns, rows, emptyText }) {
  if (!rows.length) return <SectionEmpty text={emptyText} />;

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        border: `1px solid ${Colors.border.light}`,
        borderRadius: 2.5,
        background: Colors.bg.paper,
      }}
    >
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
        <Box component="thead">
          <Box component="tr" sx={{ background: Colors.bg.hover }}>
            {columns.map((col) => (
              <Box
                key={col.key}
                component="th"
                sx={{
                  textAlign: "left",
                  p: 1.5,
                  fontSize: 13,
                  fontWeight: 900,
                  color: Colors.text.header,
                  borderBottom: `1px solid ${Colors.border.light}`,
                  whiteSpace: "nowrap",
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row, idx) => (
            <Box
              component="tr"
              key={row.id || idx}
              sx={{
                "&:hover": { background: alpha(Colors.brand.primary, 0.02) },
              }}
            >
              {columns.map((col) => (
                <Box
                  key={col.key}
                  component="td"
                  sx={{
                    p: 1.5,
                    fontSize: 14,
                    color: Colors.text.body,
                    borderBottom: `1px solid ${Colors.border.light}`,
                    whiteSpace: "nowrap",
                    verticalAlign: "top",
                  }}
                >
                  {typeof col.render === "function" ? col.render(row) : fmt(row[col.key])}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ClinicalHighlights({ patient }) {
  return (
    <Box
      sx={{
        p: 1.3,
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: alpha(Colors.brand.primary, 0.03),
      }}
    >
      <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800, mb: 1 }}>
        Simple Clinical Highlights
      </Typography>

      <Stack spacing={0.8}>
        <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
          • Patient has <b>{fmt(patient?.total_fluid_times || 0)}</b> fluid therapy record(s)
        </Typography>
        <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
          • Patient has <b>{fmt(patient?.total_medications || 0)}</b> medication record(s)
        </Typography>
        <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
          • Patient has <b>{fmt(patient?.total_investigations || 0)}</b> investigation record(s)
        </Typography>
        <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
          • Last clinical activity: <b>{formatDateTime(patient?.last_activity_at)}</b>
        </Typography>
      </Stack>
    </Box>
  );
}

function LoadingBlock({ text }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        background: Colors.bg.paper,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
      }}
    >
      <CardContent sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress sx={{ color: Colors.brand.primary }} />
        <Typography sx={{ mt: 1.5, fontWeight: 800, color: Colors.text.title }}>
          {text}
        </Typography>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 MAIN PAGE                                  */
/* -------------------------------------------------------------------------- */

export default function PatientClinicalOverviewDashboardV2() {
  const [searchText, setSearchText] = React.useState("");
  const [summaryRows, setSummaryRows] = React.useState([]);
  const [selectedPatient, setSelectedPatient] = React.useState(null);

  const [fluidHistory, setFluidHistory] = React.useState([]);
  const [medicationHistory, setMedicationHistory] = React.useState([]);
  const [investigationHistory, setInvestigationHistory] = React.useState([]);

  const [loadingSummary, setLoadingSummary] = React.useState(true);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [tabValue, setTabValue] = React.useState(0);

  const fetchSummaryData = React.useCallback(async (search = "") => {
    try {
      setError("");
      setLoadingSummary(true);

      const fluidUrl = search
        ? `${API.fluidSummary}&search=${encodeURIComponent(search)}`
        : API.fluidSummary;

      const medicationUrl = search
        ? `${API.medicationSummary}&search=${encodeURIComponent(search)}`
        : API.medicationSummary;

      const investigationUrl = search
        ? `${API.investigationSummary}?search=${encodeURIComponent(search)}`
        : API.investigationSummary;

      const [fluidRes, medicationRes, investigationRes] = await Promise.all([
        http.get(fluidUrl),
        http.get(medicationUrl),
        http.get(investigationUrl),
      ]);

      const merged = buildMergedPatientMap(
        toArray(fluidRes.data),
        toArray(medicationRes.data),
        toArray(investigationRes.data)
      );

      setSummaryRows(merged);

      setSelectedPatient((prev) => {
        if (!prev?.patient_uhid) return prev;
        return (
          merged.find(
            (row) => normalizeKey(row.patient_uhid) === normalizeKey(prev.patient_uhid)
          ) || prev
        );
      });
    } catch (err) {
      console.error("Summary load error:", err);
      setError("Unable to load patient summary dashboard.");
      setSummaryRows([]);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchPatientDetails = React.useCallback(async (patient) => {
    if (!patient?.patient_uhid) return;

    try {
      setLoadingDetails(true);
      setError("");

      const encoded = encodeURIComponent(patient.patient_uhid);

      const [fluidRes, medicationRes, investigationRes] = await Promise.all([
        http.get(`${API.fluidHistory}&patient_uhid=${encoded}`),
        http.get(`${API.medicationHistory}&patient_uhid=${encoded}`),
        http.get(`${API.investigationHistory}?patient_uhid=${encoded}`),
      ]);

      const fluidRows = toArray(fluidRes.data);
      const medicationRows = toArray(medicationRes.data);
      const investigationRows = toArray(investigationRes.data);

      setFluidHistory(fluidRows);
      setMedicationHistory(medicationRows);
      setInvestigationHistory(investigationRows);

      const f0 = fluidRows[0] || {};
      const m0 = medicationRows[0] || {};
      const i0 = investigationRows[0] || {};

      setSelectedPatient((prev) => ({
        ...prev,
        patient_uhid:
          prev?.patient_uhid ||
          f0.patient_uhid ||
          m0.patient_uhid ||
          i0.patient_uhid ||
          "",
        patient_name:
          prev?.patient_name ||
          f0.patient_name ||
          m0.patient_name ||
          i0.patient_name ||
          "",
        admission_number:
          prev?.admission_number ||
          f0.admission_number ||
          m0.admission_number ||
          i0.admission_number ||
          "",
        doctor_name:
          prev?.doctor_name ||
          f0.doctor_name ||
          m0.doctor_name ||
          i0.doctor_name ||
          "",
        total_fluid_times: fluidRows.length || prev?.total_fluid_times || 0,
        total_medications: medicationRows.length || prev?.total_medications || 0,
        total_investigations: investigationRows.length || prev?.total_investigations || 0,
        last_fluid_at: prev?.last_fluid_at || f0.created_at || "",
        last_medication_at: prev?.last_medication_at || m0.created_at || "",
        last_investigation_at: prev?.last_investigation_at || i0.created_at || "",
        last_activity_at: getLatestDate(
          prev?.last_fluid_at || f0.created_at,
          prev?.last_medication_at || m0.created_at,
          prev?.last_investigation_at || i0.created_at
        ),
      }));
    } catch (err) {
      console.error("Details load error:", err);
      setError("Unable to load selected patient details.");
      setFluidHistory([]);
      setMedicationHistory([]);
      setInvestigationHistory([]);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSummaryData("");
  }, [fetchSummaryData]);

  const handleSearch = async () => {
    await fetchSummaryData(searchText.trim());
  };

  const handleReset = async () => {
    setSearchText("");
    setSelectedPatient(null);
    setFluidHistory([]);
    setMedicationHistory([]);
    setInvestigationHistory([]);
    setTabValue(0);
    await fetchSummaryData("");
  };

  const handleRefreshSelected = async () => {
    if (!selectedPatient) return;
    try {
      setRefreshing(true);
      await fetchPatientDetails(selectedPatient);
      await fetchSummaryData(searchText.trim());
    } finally {
      setRefreshing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const selectedOption =
    selectedPatient &&
    summaryRows.find(
      (row) => normalizeKey(row.patient_uhid) === normalizeKey(selectedPatient.patient_uhid)
    );

  const totalPatients = summaryRows.length;
  const totalFluid = summaryRows.reduce((sum, row) => sum + Number(row.total_fluid_times || 0), 0);
  const totalMedication = summaryRows.reduce(
    (sum, row) => sum + Number(row.total_medications || 0),
    0
  );
  const totalInvestigation = summaryRows.reduce(
    (sum, row) => sum + Number(row.total_investigations || 0),
    0
  );

  const fluidColumns = [
    {
      key: "fluid",
      label: "Fluid",
      render: (row) => row.fluid_label || getFluidLabel(row.fluid),
    },
    { key: "qty_ml", label: "Qty (ml)" },
    { key: "speed_ml_hr", label: "Speed (ml/hr)" },
    { key: "frequency", label: "Frequency" },
    { key: "duration", label: "Duration" },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatDateTime(row.created_at),
    },
  ];

  const medicationColumns = [
    {
      key: "type",
      label: "Type",
      render: (row) => row.type_label || getMedicationTypeLabel(row.type),
    },
    { key: "generic", label: "Generic" },
    { key: "brand", label: "Brand" },
    {
      key: "route",
      label: "Route",
      render: (row) => row.route_label || getRouteLabel(row.route),
    },
    { key: "dose", label: "Dose" },
    {
      key: "frequency",
      label: "Frequency",
      render: (row) => row.frequency_label || getFrequencyLabel(row.frequency),
    },
    { key: "duration", label: "Duration" },
  ];

  const investigationColumns = [
    {
      key: "category",
      label: "Category",
      render: (row) => fmt(row.category_label || row.category),
    },
    { key: "test_name", label: "Test Name" },
    { key: "body_part_sample", label: "Body Part / Sample" },
    { key: "result", label: "Result" },
    { key: "impression_diagnosis", label: "Impression / Diagnosis" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Chip
          label={fmt(row.status_label || row.status)}
          size="small"
          sx={{
            bgcolor: alpha(getStatusColor(row.status_label || row.status), 0.14),
            color: Colors.text.body,
            fontWeight: 800,
          }}
        />
      ),
    },
    {
      key: "date",
      label: "Date",
      render: (row) => formatDate(row.date),
    },
    { key: "remarks", label: "Remarks" },
    { key: "unit", label: "Unit" },
    { key: "normal_range", label: "Normal Range" },
    {
      key: "abnormal_flag",
      label: "Abnormal Flag",
      render: (row) => fmt(row.abnormal_flag_label || row.abnormal_flag),
    },
    { key: "ordered_by", label: "Ordered By" },
    { key: "performed_by", label: "Performed By" },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatDateTime(row.created_at),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1, md: 1.5 },
        background: Colors.bg.app,
      }}
    >
      <Stack spacing={1.4}>
        {/* HEADER */}
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${Colors.border.light}`,
            background: Colors.bg.paper,
            boxShadow: Colors.shadow.brand,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 1.8 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 20, md: 28 },
                    fontWeight: 900,
                    color: Colors.text.title,
                    letterSpacing: 0.2,
                  }}
                >
                  PATIENT CLINICAL OVERVIEW
                </Typography>
                <Typography sx={{ mt: 0.4, fontSize: 13, color: Colors.text.muted }}>
                  One professional page for patient details, fluid therapy, medication and investigation history
                </Typography>

                <Box
                  sx={{
                    mt: 1.4,
                    height: 6,
                    width: 220,
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${Colors.brand.primary}, ${Colors.brand.accentBar})`,
                  }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", lg: "auto" } }}
              >
                <Autocomplete
                  fullWidth
                  options={summaryRows}
                  value={selectedOption || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setSelectedPatient(newValue);
                      fetchPatientDetails(newValue);
                    } else {
                      setSelectedPatient(null);
                      setFluidHistory([]);
                      setMedicationHistory([]);
                      setInvestigationHistory([]);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    normalizeKey(option?.patient_uhid) === normalizeKey(value?.patient_uhid)
                  }
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    return `${option.patient_name || "No Name"} | ${option.patient_uhid || "No UHID"} | ${option.admission_number || "No Admission"}`;
                  }}
                  filterOptions={(options, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return options;
                    return options.filter((option) =>
                      [
                        option.patient_name,
                        option.patient_uhid,
                        option.admission_number,
                        option.doctor_name,
                      ]
                        .join(" ")
                        .toLowerCase()
                        .includes(q)
                    );
                  }}
                  noOptionsText="No patient found"
                  sx={{
                    minWidth: { xs: "100%", sm: 390 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.2,
                      bgcolor: Colors.bg.paper,
                      color: Colors.text.body,
                    },
                    "& .MuiSvgIcon-root": {
                      color: Colors.text.icon,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Patient"
                      placeholder="Search by patient name / UHID / admission"
                    />
                  )}
                />

                <Button
                  variant="outlined"
                  startIcon={<PrintRoundedIcon />}
                  onClick={handlePrint}
                  sx={{
                    borderRadius: 2.2,
                    fontWeight: 800,
                    borderColor: Colors.border.input,
                    color: Colors.text.body,
                    minWidth: 140,
                  }}
                >
                  Print / PDF
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* KPI ROW */}
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon={<PersonRoundedIcon />}
              label="Total Patients"
              value={totalPatients}
              sub="Merged summary"
              color={Colors.brand.primary}
              bg={alpha(Colors.brand.primary, 0.05)}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon={<WaterDropRoundedIcon />}
              label="Fluid Records"
              value={totalFluid}
              sub="Fluid therapy entries"
              color={Colors.status.success}
              bg={alpha(Colors.status.success, 0.08)}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon={<MedicationOutlinedIcon />}
              label="Medication Records"
              value={totalMedication}
              sub="Medication entries"
              color={Colors.status.info}
              bg={alpha(Colors.status.info, 0.08)}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon={<ScienceRoundedIcon />}
              label="Investigation Records"
              value={totalInvestigation}
              sub="Investigation entries"
              color={Colors.status.warning}
              bg={alpha(Colors.status.warning, 0.12)}
            />
          </Grid>
        </Grid>

        {/* SEARCH PANEL */}
        <Grid container spacing={1.2}>
          <Grid item xs={12} lg={4}>
            <Panel
              title="Search & Filter"
              icon={<SearchRoundedIcon sx={{ color: Colors.text.icon }} />}
            >
              <Stack spacing={1.2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by patient UHID or patient name"
                  placeholder="Enter patient name or UHID"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: Colors.bg.paper,
                    },
                  }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                      background: Colors.brand.primary,
                      boxShadow: Colors.shadow.brand,
                      "&:hover": { background: Colors.brand.primaryDark },
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                      color: Colors.text.body,
                      borderColor: Colors.border.input,
                    }}
                  >
                    Reset
                  </Button>
                </Stack>

                <Typography sx={{ fontSize: 12, color: Colors.text.sub, lineHeight: 1.6 }}>
                  First choose a patient. After selection, all clinical details open below in an easy layout.
                </Typography>
              </Stack>
            </Panel>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Panel
              title="Quick Dashboard Summary"
              icon={<TimelineRoundedIcon sx={{ color: Colors.text.icon }} />}
            >
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 3,
                      border: `1px solid ${Colors.border.light}`,
                      bgcolor: Colors.bg.hover,
                      minHeight: 116,
                      backgroundImage: `linear-gradient(90deg, ${alpha(
                        Colors.brand.primary,
                        0.04
                      )} 25%, ${alpha(Colors.brand.primary, 0.09)} 50%, ${alpha(
                        Colors.brand.primary,
                        0.04
                      )} 75%)`,
                      backgroundSize: "220px 100%",
                      animation: `${shimmer} 3.6s linear infinite`,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800 }}>
                      Dashboard Purpose
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: 13, color: Colors.text.body, lineHeight: 1.7 }}>
                      This page merges patient summary, fluid therapy history, medication history and investigation history into one professional clinical dashboard.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 3,
                      border: `1px solid ${Colors.border.light}`,
                      bgcolor: alpha(Colors.brand.primary, 0.03),
                      minHeight: 116,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800 }}>
                      Layman Friendly View
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: 13, color: Colors.text.body, lineHeight: 1.7 }}>
                      Important patient and doctor details are shown once at top. Clinical records are separated in clear tabs so anyone can understand quickly.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Panel>
          </Grid>
        </Grid>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {/* SUMMARY LIST */}
        <Panel
          title="Patient Summary List"
          icon={<DescriptionRoundedIcon sx={{ color: Colors.text.icon }} />}
          right={
            loadingSummary ? (
              <CircularProgress size={20} sx={{ color: Colors.brand.primary }} />
            ) : (
              <Chip
                label={`${summaryRows.length} Patients`}
                sx={{
                  bgcolor: alpha(Colors.brand.primary, 0.12),
                  color: Colors.text.body,
                  fontWeight: 900,
                }}
              />
            )
          }
        >
          {loadingSummary ? (
            <LoadingBlock text="Loading patient summary..." />
          ) : summaryRows.length === 0 ? (
            <SectionEmpty text="No patient summary found." />
          ) : (
            <HistoryTable
              rows={summaryRows}
              emptyText="No patient summary found."
              columns={[
                { key: "patient_uhid", label: "Patient UHID" },
                { key: "patient_name", label: "Patient Name" },
                { key: "admission_number", label: "Admission No" },
                { key: "doctor_name", label: "Doctor Name" },
                { key: "total_fluid_times", label: "Fluid" },
                { key: "total_medications", label: "Medication" },
                { key: "total_investigations", label: "Investigation" },
                {
                  key: "last_activity_at",
                  label: "Last Activity",
                  render: (row) => formatDateTime(row.last_activity_at),
                },
                {
                  key: "action",
                  label: "Action",
                  render: (row) => (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setSelectedPatient(row);
                        fetchPatientDetails(row);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 800,
                        background: Colors.brand.primary,
                        "&:hover": { background: Colors.brand.primaryDark },
                      }}
                    >
                      Open
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </Panel>

        {!selectedPatient ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Please select a patient from the summary list to view the professional merged clinical dashboard.
          </Alert>
        ) : loadingDetails ? (
          <LoadingBlock text="Loading full patient clinical details..." />
        ) : (
          <>
            {/* SELECTED PATIENT TOP BAR */}
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${Colors.border.light}`,
                background: Colors.bg.paper,
                boxShadow: Colors.shadow.brand,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 1.7 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1.2}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 18, md: 23 },
                        fontWeight: 900,
                        color: Colors.text.title,
                      }}
                    >
                      {fmt(selectedPatient.patient_name)}
                    </Typography>
                    <Typography sx={{ mt: 0.3, fontSize: 13, color: Colors.text.muted }}>
                      UHID: {fmt(selectedPatient.patient_uhid)} | Admission: {fmt(selectedPatient.admission_number)} | Doctor: {fmt(selectedPatient.doctor_name)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={`Fluid ${fmt(selectedPatient.total_fluid_times || 0)}`}
                      sx={{
                        bgcolor: alpha(Colors.status.success, 0.14),
                        color: Colors.text.body,
                        fontWeight: 800,
                      }}
                    />
                    <Chip
                      label={`Medication ${fmt(selectedPatient.total_medications || 0)}`}
                      sx={{
                        bgcolor: alpha(Colors.status.info, 0.14),
                        color: Colors.text.body,
                        fontWeight: 800,
                      }}
                    />
                    <Chip
                      label={`Investigation ${fmt(selectedPatient.total_investigations || 0)}`}
                      sx={{
                        bgcolor: alpha(Colors.status.warning, 0.18),
                        color: Colors.text.body,
                        fontWeight: 800,
                      }}
                    />
                    <Button
                      startIcon={<RefreshRoundedIcon />}
                      onClick={handleRefreshSelected}
                      disabled={refreshing}
                      sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        color: Colors.brand.primary,
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* PATIENT SUMMARY + MINI CHARTS */}
            <Grid container spacing={1.2}>
              <Grid item xs={12} lg={3.4}>
                <Panel
                  title="Patient & Doctor"
                  icon={<PersonRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Stack spacing={0.15}>
                    <DetailLine label="Patient" value={selectedPatient.patient_name} />
                    <DetailLine label="UHID" value={selectedPatient.patient_uhid} />
                    <DetailLine label="Admission" value={selectedPatient.admission_number} />
                    <DetailLine label="Doctor" value={selectedPatient.doctor_name} />
                    <DetailLine label="Last Fluid" value={formatDateTime(selectedPatient.last_fluid_at)} />
                    <DetailLine label="Last Medicine" value={formatDateTime(selectedPatient.last_medication_at)} />
                    <DetailLine label="Last Test" value={formatDateTime(selectedPatient.last_investigation_at)} />
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} lg={4.2}>
                <Panel
                  title="Clinical Highlights"
                  icon={<MonitorHeartRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Stack spacing={1.2}>
                    <ClinicalHighlights patient={selectedPatient} />
                    <SummaryStrip patient={selectedPatient} />
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} lg={4.4}>
                <Panel
                  title="Mini Chart Overview"
                  icon={<EventNoteRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <MiniCountChart
                    fluidCount={selectedPatient.total_fluid_times}
                    medicationCount={selectedPatient.total_medications}
                    investigationCount={selectedPatient.total_investigations}
                  />
                </Panel>
              </Grid>
            </Grid>

            {/* TABS SECTION */}
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${Colors.border.light}`,
                background: Colors.bg.paper,
                boxShadow: Colors.shadow.brand,
              }}
            >
              <CardContent sx={{ p: { xs: 1, md: 1.3 } }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, v) => setTabValue(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    mb: 1.3,
                    "& .MuiTabs-indicator": {
                      backgroundColor: Colors.brand.primary,
                      height: 4,
                      borderRadius: 999,
                    },
                  }}
                >
                  <Tab
                    icon={<WaterDropRoundedIcon />}
                    iconPosition="start"
                    label="Fluid Therapy"
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      color: Colors.text.body,
                    }}
                  />
                  <Tab
                    icon={<MedicationOutlinedIcon />}
                    iconPosition="start"
                    label="Medication History"
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      color: Colors.text.body,
                    }}
                  />
                  <Tab
                    icon={<ScienceRoundedIcon />}
                    iconPosition="start"
                    label="Investigation History"
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      color: Colors.text.body,
                    }}
                  />
                </Tabs>

                {tabValue === 0 && (
                  <Panel
                    title="Fluid Therapy History"
                    icon={<WaterDropRoundedIcon sx={{ color: Colors.status.success }} />}
                    right={
                      <Chip
                        label={`${fluidHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.success, 0.14),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                    sx={{ boxShadow: "none", border: "none" }}
                  >
                    <HistoryTable
                      rows={fluidHistory}
                      emptyText="No fluid therapy history found for this patient."
                      columns={fluidColumns}
                    />
                  </Panel>
                )}

                {tabValue === 1 && (
                  <Panel
                    title="Medication History"
                    icon={<MedicationOutlinedIcon sx={{ color: Colors.status.info }} />}
                    right={
                      <Chip
                        label={`${medicationHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.info, 0.14),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                    sx={{ boxShadow: "none", border: "none" }}
                  >
                    <HistoryTable
                      rows={medicationHistory}
                      emptyText="No medication history found for this patient."
                      columns={medicationColumns}
                    />
                  </Panel>
                )}

                {tabValue === 2 && (
                  <Panel
                    title="Investigation History"
                    icon={<ScienceRoundedIcon sx={{ color: Colors.status.warning }} />}
                    right={
                      <Chip
                        label={`${investigationHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.warning, 0.18),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                    sx={{ boxShadow: "none", border: "none" }}
                  >
                    <HistoryTable
                      rows={investigationHistory}
                      emptyText="No investigation history found for this patient."
                      columns={investigationColumns}
                    />
                  </Panel>
                )}
              </CardContent>
            </Card>

            {/* BOTTOM SUMMARY */}
            <Grid container spacing={1.2}>
              <Grid item xs={12} md={6}>
                <Panel
                  title="Clinical Completion View"
                  icon={<AssignmentRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Stack spacing={1.2}>
                    <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800 }}>
                      Clinical Data Coverage
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{
                        height: 12,
                        borderRadius: 999,
                        bgcolor: alpha(Colors.brand.primary, 0.12),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          bgcolor: Colors.brand.primary,
                        },
                      }}
                    />

                    <Typography sx={{ fontSize: 13, color: Colors.text.body, lineHeight: 1.7 }}>
                      For the selected patient, the dashboard has loaded all available fluid, medication and investigation records in one place.
                    </Typography>
                  </Stack>
                </Panel>
              </Grid>

              <Grid item xs={12} md={6}>
                <Panel
                  title="Professional View Note"
                  icon={<LocalHospitalRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Typography sx={{ fontSize: 13, color: Colors.text.body, lineHeight: 1.8 }}>
                    This layout is optimized for doctors, nurses, administrators and even non-technical users. Important patient details stay fixed at top, while clinical history is separated into easy tabs for quick reading.
                  </Typography>
                </Panel>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Box>
  );
}